import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import dynamic from 'next/dynamic';
import { enqueueSnackbar } from 'notistack';

import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';

import { theme } from '@itp/talent/theme';

import { ButtonPrimary, ButtonTextSecondary } from '../core/button';
import { NavigationGuard } from './navigationGuard';
import { FormProps } from './types';

const DynamicDraftWrapper = dynamic(() => import('./draftWrapper'), {
  loading: () => null,
  ssr: false,
});
export const Form = ({
  actionButtonsLeft,
  buttonProps,
  buttonSize = 'medium',
  captureChangeElementName,
  children,
  debug,
  defaultValues,
  draftOptions,
  error,
  formStyle,
  fullWidth,
  hasActionButtons,
  id,
  isActionButtonsAbsolute,
  mode,
  navigationGuard,
  onCancel,
  onChange,
  onIsDirtyChange,
  onSubmit,
  saveOnly,
  schema,
  stackStyle = { marginTop: 2 },
  values,
}: FormProps) => {
  const resolver = (schema && zodResolver(schema)) || undefined;
  const methods = useForm({
    defaultValues,
    mode,
    resolver,
    values,
  });
  const {
    formState: { errors, isDirty, isSubmitSuccessful, isSubmitted, isSubmitting },
    getValues,
    reset,
    setError,
  } = methods;
  const [formHasBeenEdited, setFormHasBeenEdited] = React.useState(false);
  const handleSubmitInternal = methods.handleSubmit(onSubmit ?? (() => {}));

  const handleChange = () => {
    if (isDirty && !formHasBeenEdited) {
      const currentValues = getValues();
      const hasContent = Object.entries(currentValues).some(([key, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== '' && value !== null && value !== undefined;
      });
      if (hasContent) {
        setFormHasBeenEdited(true);
      }
    }
    onChange && onChange(getValues());
  };

  const handleOnChangeCapture = (e: React.FormEvent<HTMLFormElement>) => {
    if (!captureChangeElementName) {
      return null;
    }
    methods.setValue(
      captureChangeElementName,
      (e.currentTarget.elements.namedItem(captureChangeElementName) as HTMLInputElement)?.value,
    );
  };

  React.useEffect(() => {
    if (debug)
      console.log('debug :>> ', {
        errors,
        formHasBeenEdited,
        isDirty,
        isSubmitSuccessful,
        isSubmitted,
        isSubmitting,
        reset,
        values: getValues(),
      });
  }, [
    debug,
    errors,
    isDirty,
    isSubmitSuccessful,
    isSubmitted,
    isSubmitting,
    reset,
    getValues,
    formHasBeenEdited,
  ]);

  React.useEffect(() => {
    if (error?.message) {
      setError(error?.name, {
        message: error?.message,
        type: error?.type,
      });
      enqueueSnackbar(error?.message, {
        autoHideDuration: 3000,
        variant: 'error',
      });
    }
  }, [setError, error?.message, error?.name, error?.type]);

  return (
    <FormProvider {...methods}>
      <form
        style={{
          ...(fullWidth ? { width: '100%' } : {}),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(6),
          ...formStyle,
        }}
        className="ITP-form"
        id={id}
        noValidate
        onChange={handleChange}
        onChangeCapture={handleOnChangeCapture}
        onSubmit={handleSubmitInternal}
      >
        {draftOptions && (
          <DynamicDraftWrapper
            defaultValues={defaultValues}
            draftOptions={draftOptions}
            error={error}
          />
        )}
        {children}
        {hasActionButtons && isDirty && (
          <Stack
            direction="row"
            gap={2}
            justifyContent={actionButtonsLeft ? 'start' : 'end'}
            position={isActionButtonsAbsolute ? 'absolute' : undefined}
            right={isActionButtonsAbsolute ? theme.spacing(2) : undefined}
            sx={stackStyle}
          >
            {!saveOnly && (
              <ButtonTextSecondary
                onClick={() => {
                  reset(defaultValues);
                  setFormHasBeenEdited(false);
                  if (typeof onCancel === 'function') onCancel(defaultValues);
                }}
                aria-label={`Cancel ${id}`}
                size={buttonSize}
                {...buttonProps}
              >
                Cancel
              </ButtonTextSecondary>
            )}
            <ButtonPrimary
              aria-label={`Submit ${id}`}
              size={buttonSize}
              type="submit"
              {...buttonProps}
            >
              Save
            </ButtonPrimary>
          </Stack>
        )}
      </form>
      {navigationGuard && (
        <NavigationGuard
          customMessage={navigationGuard.customMessage}
          enabled={navigationGuard.enabled}
          formHasBeenEdited={formHasBeenEdited}
          isDirty={isDirty}
          isSubmitSuccessful={isSubmitSuccessful}
          isSubmitting={isSubmitting}
        />
      )}
    </FormProvider>
  );
};
